import { type FC, useCallback, useEffect, useState } from "react";
import { Input, Spinner } from "@snapchat/snap-design-system";
import { CrossIcon, AddIcon } from "@snapchat/snap-design-system-icons";
import { Button } from "@snapchat/snap-design-system";
import { DisplayComponent } from "../../../common/components";
import { KeyboardKey } from "../../../common/model";
import { useInputState } from "../../../core/validation";
import type { NewGroup } from "../../../dashboard/models";
import { GroupsRestrictions } from "../../model";

import {
    AddLensGroupButton,
    CreateButton,
    InputContainer,
    NewGroupNameLabel,
    ResetButton,
    ResetIconContiner,
    SpinnerContainer,
    newGroupButtonOverrides,
} from "./NewLensGroupInput.styles";

interface NewLensGroupInputProps {
    onCreate: (groupName: NewGroup) => void;
    isCreated: boolean;
}

export const NewLensGroupInput: FC<NewLensGroupInputProps> = ({ onCreate, isCreated }) => {
    const [editMode, setEditMode] = useState(false);

    const { inputProps, isValid, reset } = useInputState<string>({
        defaultValue: "",
        validator: (newValue) => Boolean(newValue?.trim()),
        error: "Name is required",
    });

    const enableEditMode = useCallback(() => {
        setEditMode(true);
    }, []);

    const resetInput = useCallback(() => {
        setEditMode(false);
        reset();
    }, [reset]);

    const handleCreate = useCallback(() => {
        const lensGroupName = inputProps?.value?.trim();
        if (isValid) {
            setEditMode(false);
            onCreate({
                lenses: [],
                name: lensGroupName,
            });
            reset();
        }
    }, [inputProps.value, isValid, onCreate, reset]);

    const onKeyPress = useCallback(
        (e: KeyboardEvent) => {
            if (e.key == KeyboardKey.Enter) {
                handleCreate();
            }
        },
        [handleCreate],
    );

    useEffect(() => {
        if (editMode) {
            document.addEventListener("keydown", onKeyPress);
        }

        return () => {
            document.removeEventListener("keydown", onKeyPress);
        };
    }, [onKeyPress, editMode]);

    const newGroupName = inputProps.value;

    return (
        <InputContainer>
            <SpinnerContainer show={isCreated}>
                <Spinner />
            </SpinnerContainer>
            <DisplayComponent show={!editMode && !isCreated}>
                <AddLensGroupButton alignItems="baseline" onClick={enableEditMode}>
                    <Button className={newGroupButtonOverrides} type="link">
                        <AddIcon />
                        New Lens Group
                    </Button>
                </AddLensGroupButton>
            </DisplayComponent>
            <DisplayComponent show={editMode}>
                <Input
                    {...inputProps}
                    maxLength={GroupsRestrictions.maxGroupNameLength}
                    data-testid="new-group-input"
                />
                <ResetIconContiner>
                    <DisplayComponent show={!!newGroupName}>
                        <NewGroupNameLabel>{newGroupName}</NewGroupNameLabel>
                        <ResetButton
                            icon={<CrossIcon />}
                            onClick={resetInput}
                            isPending={isCreated}
                            isDisabled={isCreated}
                        />
                    </DisplayComponent>
                </ResetIconContiner>
                <CreateButton type="link" onClick={handleCreate}>
                    Create
                </CreateButton>
            </DisplayComponent>
        </InputContainer>
    );
};
